<template>
  <div class="step-form">
    <h2 class="step-form__title">{{ $t('step3.title') }}</h2>
    <p class="step-form__subtitle">{{ $t('step3.subtitle') }}</p>

    <div class="hotels-scroll">
      <div v-for="(hotel, idx) in formData.hotels" :key="idx" class="hotel-card">
        <div class="hotel-card__header">
          <span class="hotel-card__number">{{ $t('step3.hotelNumber', { n: idx + 1 }) }}</span>
          <button v-if="formData.hotels.length > 1" class="hotel-card__remove" @click="removeHotel(idx)">
            {{ $t('step3.removeHotel') }}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div class="hotel-card__grid">
          <div class="field">
            <label class="field__label">{{ $t('step3.street') }}</label>
            <input class="field__input" type="text" :value="hotel.street"
                   :placeholder="$t('step3.streetPlaceholder')"
                   @input="updateHotel(idx, 'street', $event.target.value)" />
          </div>
          <div class="field">
            <label class="field__label">{{ $t('step3.houseNumber') }}</label>
            <input class="field__input" type="text" :value="hotel.houseNumber"
                   :placeholder="$t('step3.houseNumberPlaceholder')"
                   @input="updateHotel(idx, 'houseNumber', $event.target.value)" />
          </div>
          <div class="field">
            <label class="field__label">{{ $t('step3.zip') }}</label>
            <input class="field__input" type="text" :value="hotel.zip"
                   :placeholder="$t('step3.zipPlaceholder')"
                   @input="updateHotel(idx, 'zip', $event.target.value)" />
          </div>
          <div class="field">
            <label class="field__label">{{ $t('step3.city') }}</label>
            <input class="field__input" type="text" :value="hotel.city"
                   :placeholder="$t('step3.cityPlaceholder')"
                   @input="updateHotel(idx, 'city', $event.target.value)" />
          </div>
          <div class="field field--wide">
            <label class="field__label">{{ $t('step3.description') }}</label>
            <input class="field__input" type="text" :value="hotel.description"
                   :placeholder="$t('step3.descriptionPlaceholder')"
                   @input="updateHotel(idx, 'description', $event.target.value)" />
          </div>
          <div class="field field--wide">
            <label class="field__label">{{ $t('step3.units') }}</label>
            <input class="field__input" type="number" min="1" :value="hotel.units"
                   :placeholder="$t('step3.unitsPlaceholder')"
                   @input="updateHotel(idx, 'units', parseInt($event.target.value) || 1)" />
          </div>
        </div>
      </div>

      <button class="add-hotel-btn" @click="addHotel">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
        {{ $t('step3.addHotel') }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HotelAddresses',
  props: { formData: Object },
  methods: {
    updateHotel(idx, field, value) {
      const hotels = this.formData.hotels.map((h, i) =>
        i === idx ? { ...h, [field]: value } : { ...h }
      )
      this.$emit('update', { hotels })
    },
    addHotel() {
      const hotels = [...this.formData.hotels, { street: '', houseNumber: '', zip: '', city: '', description: '', units: 1 }]
      this.$emit('update', { hotels, hotelCount: hotels.length })
    },
    removeHotel(idx) {
      const hotels = this.formData.hotels.filter((_, i) => i !== idx)
      this.$emit('update', { hotels, hotelCount: hotels.length })
    },
  },
}
</script>

<style lang="scss" scoped>
.step-form {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  &__title {
    font-size: clamp(1.2rem, 3vw, 2rem);
    font-weight: 800;
    margin-bottom: 0.4rem;
    background: linear-gradient(135deg, #fff 30%, var(--gold-light));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &__subtitle {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.4);
    margin-bottom: 1.2rem;
  }
}

.hotels-scroll {
  max-height: 50vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }

  @media (max-width: 600px) {
    max-height: 55vh;
    padding-right: 0;
  }
}

.hotel-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 10px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  &__number {
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--gold);
  }

  &__remove {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border: none;
    border-radius: 6px;
    background: rgba(234, 84, 85, 0.08);
    color: #ea5455;
    font-size: 0.72rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
    &:hover { background: rgba(234, 84, 85, 0.15); }
  }

  &__grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 8px;
  }
}

.field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;

  &--wide { grid-column: 1 / -1; }

  &__label {
    font-size: 0.68rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.35);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__input {
    width: 100%;
    box-sizing: border-box;
    padding: 9px 11px;
    border: 1.5px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.03);
    color: #fff;
    font-size: 0.82rem;
    outline: none;
    transition: all 0.2s;

    &::placeholder { color: rgba(255, 255, 255, 0.15); }

    &:focus {
      border-color: var(--gold);
      background: rgba(208, 176, 146, 0.04);
      box-shadow: 0 0 0 2px rgba(208, 176, 146, 0.08);
    }

    &[type="number"] {
      -moz-appearance: textfield;
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button { -webkit-appearance: none; }
    }
  }
}

.add-hotel-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border: 1.5px dashed rgba(208, 176, 146, 0.2);
  border-radius: 12px;
  background: transparent;
  color: var(--gold);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--gold);
    background: rgba(208, 176, 146, 0.04);
  }
}

@media (max-width: 600px) {
  .hotel-card {
    padding: 12px;
    border-radius: 12px;

    &__grid {
      grid-template-columns: 1fr;
      gap: 6px;
    }
  }

  .field--wide {
    grid-column: 1;
  }

  .add-hotel-btn {
    padding: 10px;
    font-size: 0.78rem;
  }
}
</style>
